import LiquidationPriceTooltipContent from 'src/components/Tooltip/DefinitionTooltip/content/components/LiquidationPriceTooltipContent';
import ReferencePriceTooltipContent from 'src/components/Tooltip/DefinitionTooltip/content/components/ReferencePriceTooltipContent';
import { DefinitionTooltipConfig } from 'src/components/Tooltip/DefinitionTooltip/types';
import Link from 'src/components/primitives/Link/Link';
import { PRETTY_ACCOUNT_RENT_MINIMUM } from 'src/constants/constants';

const openPositionsTooltips = {
	openPositionsLiqPrice: {
		title: 'Est. Liq. Price',
		body: (
			<>
				<LiquidationPriceTooltipContent />
				<p>It is recommended to de-risk if this price is near.</p>
			</>
		),
	},
	openPositionsMarkPercentChange: {
		title: 'Mark % Change',
		body: 'Percentage change in asset price since your entry.',
	},
	openPositionsMargin: {
		title: 'Position Margin',
		body: (
			<>
				<p>For Cross position, Margin represents Initial Margin.</p>
				<p>
					For Isolated position, Margin represents Initial Margin + changes to
					margin + unrealized P&L (i.e. Total Collateral).
				</p>
			</>
		),
	},
} as const satisfies Record<string, DefinitionTooltipConfig>;

const positionHistoryTooltips = {
	positionHistoryPricePercentChange: {
		title: 'Price % Change',
		body: 'Percentage change in asset price since your entry.',
	},
	positionHistoryPnl: {
		title: 'P&L',
		body: 'P&L from closing/reducing position, including fees.',
	},
} as const satisfies Record<string, DefinitionTooltipConfig>;

/**
 * Tooltips used across both open orders and order history tables.
 */
const ordersTooltips = {
	ordersReferencePrice: {
		title: 'Reference Price',
		body: (
			<ReferencePriceTooltipContent>
				View Reference Price in market details.
			</ReferencePriceTooltipContent>
		),
	},
} as const satisfies Record<string, DefinitionTooltipConfig>;

const balancesTooltips = {
	balancesLiquidationPrice: {
		title: 'Est. Liq. Price',
		body: <LiquidationPriceTooltipContent />,
	},
} as const satisfies Record<string, DefinitionTooltipConfig>;

const unrealizedPnlTooltips = {
	unrealizedPnlClaimablePnl: {
		title: 'Claimable P&L',
		body: (
			<>
				<p>
					Claimable P&L is the portion of your position&apos;s Unsettled P&L
					that can be settled.
				</p>
				<p>
					Positive P&L is settled to your account balance and negative P&L is
					settled to the per-market pool of funds.
				</p>
				<Link
					href="https://docs.drift.trade/profit-loss/profit-loss-intro#settling-and-claiming-your-pl"
					openInNewTab
				>
					Learn more
				</Link>
			</>
		),
	},
	unrealizedPnlClaim: {
		title: 'Claim P&L',
		body: 'Claim your unsettled funding and P&L. This will not impact your open positions.',
	},
} as const satisfies Record<string, DefinitionTooltipConfig>;

const fundingHistoryTooltips = {
	fundingHistoryDirection: {
		title: 'Direction',
		body: 'Position direction at time of funding payment.',
	},
	fundingHistoryPositionSize: {
		title: 'Position Size',
		body: 'Position size at the time of funding payment.',
	},
} as const satisfies Record<string, DefinitionTooltipConfig>;

const accountsTooltips = {
	accountsReclaimableRent: {
		title: 'Reclaimable Rent',
		body: (
			<>
				<p>
					The base account rent ({PRETTY_ACCOUNT_RENT_MINIMUM} SOL) can only be
					claimed by deleting your account.
				</p>
				<p>
					Any extra rent contributed due to dynamic fees, will be claimable
					after 13 days of account creation.
				</p>
				<Link
					href="https://docs.drift.trade/getting-started/managing-subaccount"
					openInNewTab
				>
					Learn more
				</Link>
			</>
		),
	},
} as const satisfies Record<string, DefinitionTooltipConfig>;

export const tableTooltips = {
	...openPositionsTooltips,
	...positionHistoryTooltips,
	...balancesTooltips,
	...ordersTooltips,
	...unrealizedPnlTooltips,
	...fundingHistoryTooltips,
	...accountsTooltips,
} as const satisfies Record<string, DefinitionTooltipConfig>;
